import type AffectedProductListElement from './affected-product-list-element'
import type AutocompleteElement from '@github/auto-complete-element'
import {on} from 'delegated-events'
import {remoteForm} from '@github-ui/remote-form'
import {validate} from '../behaviors/html-validation'
import {onInput} from '@github-ui/onfocus'
import {observe} from '@github/selector-observer'
import {TemplateInstance} from '@github/template-parts'

// Used to power the "add collaborator" sidebar widget;
// the most basic of remote forms, we just submit and reset the form state.
remoteForm('.js-add-repository-advisory-collaborator', async function (form, wants) {
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]')
  const warningBanner = document.querySelector<HTMLElement>('.js-advisory-collaborator-status')!
  const infoContainer = warningBanner.querySelector('.js-advisory-collaborator-status div.Banner-message')!
  const infoText = warningBanner.querySelector('p.Banner-title')!

  // Ensure the info text has no padding on the left to align with the icon
  infoContainer.classList.add('pl-0')

  if (submitButton) {
    submitButton.disabled = true
  }

  const response = await wants.html()
  try {
    infoText.textContent = ''
    warningBanner.hidden = true

    if (response?.text?.trim()) {
      const parsed = JSON.parse(response.text)
      const data = parsed.data || {}
      if (data.notice) {
        // show flash messages returned from the server
        flashMessage(data.notice, 'info')
      } else if (data.error) {
        // show any warning messages returned from the server
        infoText.textContent = data.error
        warningBanner.hidden = false
      }
    } else {
      // eslint-disable-next-line i18n-text/no-en
      flashMessage('An unexpected error occurred. Please try again.', 'error')
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing response:', error)

    infoText.textContent = ''
    warningBanner.hidden = true
    // eslint-disable-next-line i18n-text/no-en
    flashMessage('An unexpected error occurred. Please try again.', 'error')
  } finally {
    // In the future, let's annotate the form with a loading class.
    form.reset()
  }
})

// Remove a selected CWE from the list
on('click', '.js-remove-cwe-link', function (event) {
  const cwe = (event.target as Element).closest<HTMLElement>('.js-cwe-link')!
  cwe.remove()
  autoSuggestTitle()
})

// Add a selected CWE to the list and clear the search box
on('auto-complete-change', '.js-add-cwe-link-auto-complete', function (event) {
  const autoComplete = event.target as AutocompleteElement
  const cwe = autoComplete.querySelector<HTMLElement>(`.js-cwe-link[data-cwe-id="${autoComplete.value}"]`)
  if (!cwe) {
    return
  }

  const cweList = document.querySelector<HTMLElement>('.js-cwe-list')!
  const cweIdInput = cwe.querySelector<HTMLInputElement>('.js-cwe-id')!
  let cweAlreadyLinked = false

  // Check for duplicate repos
  for (const input of cweList.querySelectorAll<HTMLInputElement>('.js-cwe-id')) {
    if (input.value === cweIdInput.value) {
      cweAlreadyLinked = true
      break
    }
  }

  if (!cweAlreadyLinked) {
    cweIdInput.disabled = false
    cweList.append(cwe)
    cwe.hidden = false
  }

  autoComplete.value = ''
})

// Auto-suggest a title for the advisory based on $CWE-name + in + $package-name
export function autoSuggestTitle() {
  const titleInput = document.querySelector<HTMLInputElement>('.js-advisory-form-title')!
  const cweList = document.querySelector<HTMLElement>('.js-cwe-list')!
  const packageName = document.querySelector<HTMLInputElement>('.js-advisory-package-name')!

  // Don't auto-suggest if the user has already set a title
  if (titleInput.hasAttribute('data-user-populated')) {
    return
  }

  // Only auto-suggest if we have at least one CWE and a package name
  if (cweList.childElementCount > 0 && packageName.value !== '') {
    const cweTitlesList = []
    for (const cweItem of cweList.children) {
      cweTitlesList.push(cweItem.getAttribute('data-cwe-name'))
    }

    titleInput.value = `${cweTitlesList.join(' and ')} in ${packageName.value}`
  } else {
    titleInput.value = ''
  }

  const form = titleInput.closest<HTMLFormElement>('form')
  if (form) {
    validate(form)
  }
}
on('auto-complete-change', '.js-add-cwe-link-auto-complete', autoSuggestTitle)
on('change', '.js-advisory-package-name', autoSuggestTitle)

export function hideFormGroupError(event: Event) {
  const input = event.target as HTMLInputElement
  const erroredFormGroup = input.closest('.form-group.errored')
  if (erroredFormGroup) {
    erroredFormGroup.classList.remove('errored')
  }
}
on('focusout', '.js-advisory-form-title', hideFormGroupError)
on('change', '.js-advisory-form-title', function (event) {
  event.currentTarget.toggleAttribute('data-user-populated', true)
})

on('change', '.js-ecosystem-selection', function (event) {
  const selection = event.currentTarget as HTMLSelectElement
  const otherInputField = selection.parentElement!.querySelector<HTMLInputElement>('.js-ecosystem-other')!
  otherInputField.hidden = selection.value !== 'other'
  validate(selection.form!)
})

function descriptionChanged(element: HTMLTextAreaElement) {
  let isValid = true
  if (element.getAttribute('data-templated-content') === element.value) {
    isValid = false
  }

  if (isValid) {
    element.setCustomValidity('')
  } else {
    // eslint-disable-next-line i18n-text/no-en
    element.setCustomValidity('Please fill out the details')
  }

  validate(element.form!)
}

function flashMessage(message: string, type = 'notice') {
  const template = new TemplateInstance(document.querySelector<HTMLTemplateElement>('.js-flash-template')!, {
    className: `flash-${type}`,
    message,
  })
  const node = document.importNode(template, true)
  const flashContainer = document.querySelector<HTMLElement>('#js-flash-container')!
  for (const child of flashContainer.children) {
    if (!child.classList.contains('js-flash-template')) {
      flashContainer.removeChild(child)
    }
  }
  flashContainer.appendChild(node)
}

onInput('#repository_advisory_description', function (event) {
  const textArea = event.currentTarget as HTMLTextAreaElement
  descriptionChanged(textArea)
})

observe('#repository_advisory_description', element => {
  // Run validation on description elements as they appear on the page
  descriptionChanged(element as HTMLTextAreaElement)
})

on('affected-product-list-change', 'affected-product-list', function (event) {
  const affectedProductListElement = event.target as AffectedProductListElement
  const enclosingForm = affectedProductListElement.closest<HTMLFormElement>('form')
  if (enclosingForm) {
    validate(enclosingForm)
  }
})

remoteForm('.js-remove-repository-advisory-collaborator', async function (form, wants) {
  const response = await wants.html()
  try {
    if (response?.text?.trim()) {
      const parsed = JSON.parse(response.text)
      const data = parsed.data || {}
      if (data.notice) {
        // show flash messages returned from the server
        flashMessage(data.notice, 'info')
      }
    } else {
      // eslint-disable-next-line i18n-text/no-en
      flashMessage('An unexpected error occurred. Please try again.', 'error')
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing response:', error)

    // eslint-disable-next-line i18n-text/no-en
    flashMessage('An unexpected error occurred. Please try again.', 'error')
  } finally {
    // In the future, let's annotate the form with a loading class.
    form.reset()
  }
})
